import { buscarBytesRemotos, salvarBlobComoArquivo } from '@/shared/utils/browserActions';
import { esperar } from '@/shared/utils/esperar';
import { proximoIntervaloComBackoff } from '@/shared/utils/proximoIntervaloComBackoff';
import { assertUrlNavegavelSegura } from '@/shared/utils/urlSegura';
import { solicitacaoPdfCertificadoAcessosSchema } from '@/shared/validation/certificadoAcessosDtoSchemas';
import {
  PDF_CERTIFICADO_POLLING_BACKOFF_FATOR,
  PDF_CERTIFICADO_POLLING_INTERVALO_MAX_MS,
  PDF_CERTIFICADO_POLLING_INTERVALO_MS,
  PDF_CERTIFICADO_POLLING_TETO_MS,
} from '../constants/pdfCertificadoAcessosPolling';
import type { SolicitacaoPdfCertificadoAcessos } from '../types/certificadoAcessosTypes';

// O PDF de um Certificado de Acessos — o da citação e o da sentença — vem sempre do servidor: é o
// único arquivo cujo hash o `/autenticar` reconhece. Estas peças são o protocolo comum, usado também
// pelo Certificado da Negociação.

/**
 * A resposta do `gerar-pdf` lida com segurança.
 *
 * `safeParse`, não `parse`: a falha vira a mensagem do toast, e um ZodError cru mostraria ao
 * usuário o caminho do campo em vez de "erro ao gerar o PDF".
 */
export async function lerSolicitacaoPdfCertificado(
  response: Response,
  mensagemDeErro: string,
  rotuloDoLink: string,
): Promise<SolicitacaoPdfCertificadoAcessos> {
  if (!response.ok) throw new Error(mensagemDeErro);
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new Error(mensagemDeErro);
  }
  const resultado = solicitacaoPdfCertificadoAcessosSchema.safeParse(body);
  if (!resultado.success) throw new Error(mensagemDeErro);
  const solicitacao = resultado.data.data;
  if (solicitacao.status === 'GENERATING') return { status: 'GENERATING' };
  return {
    status: 'READY',
    pdfUrl: assertUrlNavegavelSegura(solicitacao.pdfUrl, rotuloDoLink),
  };
}

/**
 * Pergunta ao servidor até o PDF existir, com backoff e teto de relógio.
 *
 * A primeira pergunta é a do clique — nunca há uma requisição de "aquecimento" antes dela, e
 * nenhuma repergunta acontece quando o servidor já responde `READY`.
 *
 * O `signal` interrompe a espera entre reperguntas. Sem ele, abortar o thunk só descartava o
 * resultado: este laço seguia fazendo `POST .../gerar-pdf` por até 2 min, e a trava solta pelo
 * `rejected` deixava um novo clique abrir uma SEGUNDA cadeia ao lado da órfã (review do #1160,
 * COR-01 + CodeRabbit).
 */
async function urlDoPdfQuandoPronto(
  solicitar: (signal: AbortSignal) => Promise<SolicitacaoPdfCertificadoAcessos>,
  signal: AbortSignal,
): Promise<string> {
  const limite = Date.now() + PDF_CERTIFICADO_POLLING_TETO_MS;
  let intervalo = PDF_CERTIFICADO_POLLING_INTERVALO_MS;
  let solicitacao = await solicitar(signal);

  while (solicitacao.status === 'GENERATING') {
    if (Date.now() >= limite) {
      throw new Error(
        'O PDF ainda está sendo gerado. Tente novamente em alguns instantes — nada foi perdido.',
      );
    }
    await esperar(intervalo, signal);
    intervalo = proximoIntervaloComBackoff(
      intervalo,
      PDF_CERTIFICADO_POLLING_BACKOFF_FATOR,
      PDF_CERTIFICADO_POLLING_INTERVALO_MAX_MS,
    );
    solicitacao = await solicitar(signal);
  }

  return solicitacao.pdfUrl;
}

/**
 * Baixa o PDF emitido pelo servidor: pede a geração, espera ficar pronto e salva os bytes.
 *
 * Os bytes são buscados e salvos como Blob (`credentials: 'omit'`, o bucket de certificados
 * autoriza a origem do front) em vez de navegar para a URL assinada: ela sai sem
 * `Content-Disposition: attachment`, então navegar abriria o visualizador de PDF por cima da
 * tela. Nada de `window.open` depois do `await`: o bloqueador de pop-up engoliria a janela e o
 * usuário ficaria sem arquivo e sem aviso.
 */
export async function baixarPdfDoServidor(
  solicitar: (signal: AbortSignal) => Promise<SolicitacaoPdfCertificadoAcessos>,
  nomeArquivo: string,
  signal: AbortSignal,
): Promise<void> {
  const pdfUrl = await urlDoPdfQuandoPronto(solicitar, signal);
  const bytes = await buscarBytesRemotos(pdfUrl, signal);
  // O abort do RTK descarta o resultado do thunk, mas não para este corpo: sem a checagem, o
  // arquivo de quem já saiu da tela ainda iria para o disco.
  signal.throwIfAborted();
  salvarBlobComoArquivo(bytes, nomeArquivo);
}
